import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of, switchMap, catchError } from 'rxjs';
import { environment } from '../../../../environment/environment';
import { EventExtraSettingsDTO } from '../../../component-data-source/data/event/event_extra';
import { eventCategoryFromFormValue } from '../../../component-data-source/data/event/event-category-form.util';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function resolveEventCategoryFromFormData(
  data: Record<string, unknown>
): string | null {
  if (!('event_category' in data)) {
    return null;
  }
  return eventCategoryFromFormValue(data['event_category']);
}

/**
 * PATCH event extra-settings category after event create/update when form included event_category.
 */
export function patchEventCategoryFromForm(
  http: HttpClient,
  eventGuid: string,
  data: Record<string, unknown>,
  existingExtra?: Partial<EventExtraSettingsDTO>
): Observable<unknown> {
  if (!UUID_REGEX.test(eventGuid)) {
    return of(null);
  }
  if (!('event_category' in data)) {
    return of(null);
  }

  const category = resolveEventCategoryFromFormData(data);
  const payload: EventExtraSettingsDTO = {
    category,
    smoke_development: existingExtra?.smoke_development ?? false,
    cleaning: existingExtra?.cleaning ?? false,
    cash_list_required: existingExtra?.cash_list_required ?? false,
    certificates_created: existingExtra?.certificates_created ?? false,
    cash_list_packed_date: existingExtra?.cash_list_packed_date ?? null,
  };

  return http.patch(
    `${environment.apiUrl}/events/${eventGuid}/extra-settings`,
    payload
  );
}

/** Load current extra settings before merging category patch on edit. */
export function loadEventExtraSettings(
  http: HttpClient,
  eventGuid: string
): Observable<EventExtraSettingsDTO | null> {
  if (!UUID_REGEX.test(eventGuid)) {
    return of(null);
  }
  return http
    .get<EventExtraSettingsDTO>(
      `${environment.apiUrl}/events/${eventGuid}/extra-settings`
    )
    .pipe(catchError(() => of(null)));
}

export function patchEventCategoryAfterSave(
  http: HttpClient,
  eventGuid: string,
  data: Record<string, unknown>
): Observable<unknown> {
  if (!('event_category' in data)) {
    return of(null);
  }
  return loadEventExtraSettings(http, eventGuid).pipe(
    switchMap((extra) =>
      patchEventCategoryFromForm(http, eventGuid, data, extra ?? undefined)
    ),
    catchError(() => of(null))
  );
}

function addGuidToSet(guids: Set<string>, value: unknown): void {
  if (typeof value === 'string' && UUID_REGEX.test(value)) {
    guids.add(value);
  }
}

/** All session GUIDs from create API response (linked series). */
export function extractEventGuidsFromCreateResponse(
  payload: Record<string, unknown>
): string[] {
  const guids = new Set<string>();
  const list = payload['event_guids'];
  if (Array.isArray(list)) {
    for (const g of list) {
      addGuidToSet(guids, g);
    }
  }
  addGuidToSet(guids, payload['primary_event_guid']);
  addGuidToSet(guids, payload['guid']);
  return Array.from(guids);
}

/** Related session GUIDs from edit form (parent + sub-events / serie-datoer). */
export function collectRelatedEventGuidsFromFormData(
  data: Record<string, unknown>,
  mainEventGuid: string
): string[] {
  const guids = new Set<string>();
  addGuidToSet(guids, mainEventGuid);

  const subEvents = data['sub_events'];
  if (Array.isArray(subEvents)) {
    for (const row of subEvents) {
      if (row && typeof row === 'object') {
        addGuidToSet(guids, (row as Record<string, unknown>)['sub_event_guid']);
      }
    }
  }

  const additional = data['additional_dates'];
  if (Array.isArray(additional)) {
    for (const row of additional) {
      if (row && typeof row === 'object') {
        addGuidToSet(guids, (row as Record<string, unknown>)['sub_event_guid']);
      }
    }
  }

  let seriesRaw: unknown = data['series_dates'];
  if (typeof seriesRaw === 'string' && seriesRaw.trim() !== '') {
    try {
      seriesRaw = JSON.parse(seriesRaw);
    } catch {
      seriesRaw = null;
    }
  }
  if (Array.isArray(seriesRaw)) {
    for (const entry of seriesRaw) {
      if (entry && typeof entry === 'object') {
        addGuidToSet(
          guids,
          (entry as Record<string, unknown>)['series_date_guid']
        );
      }
    }
  }

  return Array.from(guids);
}

/** PATCH category on every event in a series (create/update). */
export function patchEventCategoryForEventGuids(
  http: HttpClient,
  eventGuids: string[],
  data: Record<string, unknown>
): Observable<unknown> {
  if (!('event_category' in data)) {
    return of(null);
  }
  const unique = [
    ...new Set(eventGuids.filter((g) => typeof g === 'string' && UUID_REGEX.test(g))),
  ];
  if (unique.length === 0) {
    return of(null);
  }
  return forkJoin(
    unique.map((guid) =>
      patchEventCategoryAfterSave(http, guid, data).pipe(catchError(() => of(null)))
    )
  );
}
